import React, { useMemo, useState } from "react";

function parseNum(v) {
  const s = String(v ?? "")
    .trim()
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}
function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

/**
 * Abattements simplifiés (résidence secondaire / investissement, hors exonérations)
 * - IR : exonération totale après 22 ans (abattement progressif)
 * - PS : exonération totale après 30 ans (abattement progressif)
 * Ici on fait un modèle simple: on calcule une base taxable "restante" en %
 * (Ce n'est pas un calcul notarial exact, mais utile pour une estimation).
 */
function remainingBaseIRPct(years) {
  const y = Math.max(0, years);
  if (y <= 5) return 100;
  if (y >= 22) return 0;

  // Approximation linéaire de 6 à 21 ans
  // (en vrai, c'est par paliers avec taux précis)
  const span = 22 - 5; // 17
  const done = y - 5;
  const remaining = 100 * (1 - done / span);
  return clamp(remaining, 0, 100);
}

function remainingBasePSPct(years) {
  const y = Math.max(0, years);
  if (y <= 5) return 100;
  if (y >= 30) return 0;

  // Approximation linéaire de 6 à 29 ans
  const span = 30 - 5; // 25
  const done = y - 5;
  const remaining = 100 * (1 - done / span);
  return clamp(remaining, 0, 100);
}

export default function CalculPlusValueImmobiliere() {
  // Achat
  const [purchasePrice, setPurchasePrice] = useState("180000");
  const [purchaseFees, setPurchaseFees] = useState("13500"); // notaire etc
  const [works, setWorks] = useState("0"); // travaux (option)
  const [otherCosts, setOtherCosts] = useState("0"); // frais divers (option)

  // Vente
  const [salePrice, setSalePrice] = useState("220000");
  const [agencyFees, setAgencyFees] = useState("0"); // frais d’agence à la vente (si déduits)
  const [otherSaleCosts, setOtherSaleCosts] = useState("0"); // diagnostics, etc

  // Détention
  const [yearsHeld, setYearsHeld] = useState("8");

  // Mode fiscal simple
  const [applyTaxEstimation, setApplyTaxEstimation] = useState(true);

  const computed = useMemo(() => {
    const buy = clamp(parseNum(purchasePrice), 0, 100_000_000);
    const buyFees = clamp(parseNum(purchaseFees), 0, 10_000_000);
    const w = clamp(parseNum(works), 0, 50_000_000);
    const buyOther = clamp(parseNum(otherCosts), 0, 10_000_000);

    const sell = clamp(parseNum(salePrice), 0, 100_000_000);
    const ag = clamp(parseNum(agencyFees), 0, 10_000_000);
    const sellOther = clamp(parseNum(otherSaleCosts), 0, 10_000_000);

    const y = clamp(parseNum(yearsHeld), 0, 60);

    const acquisitionCost = buy + buyFees + w + buyOther;
    const netSale = Math.max(0, sell - ag - sellOther);

    const grossGain = netSale - acquisitionCost;

    // Estimation impôt + prélèvements sociaux (mode simple)
    // IR ~ 19%, PS ~ 17,2% sur base taxable restante (approx)
    const irBasePct = remainingBaseIRPct(y);
    const psBasePct = remainingBasePSPct(y);

    const taxableForIR = Math.max(0, grossGain) * (irBasePct / 100);
    const taxableForPS = Math.max(0, grossGain) * (psBasePct / 100);

    const estIR = taxableForIR * 0.19;
    const estPS = taxableForPS * 0.172;
    const estTotalTax = estIR + estPS;

    const netAfterTax = grossGain - (applyTaxEstimation ? estTotalTax : 0);

    return {
      buy,
      buyFees,
      w,
      buyOther,
      sell,
      ag,
      sellOther,
      years: y,
      acquisitionCost,
      netSale,
      grossGain,
      irBasePct,
      psBasePct,
      estIR,
      estPS,
      estTotalTax,
      netAfterTax,
    };
  }, [
    purchasePrice,
    purchaseFees,
    works,
    otherCosts,
    salePrice,
    agencyFees,
    otherSaleCosts,
    yearsHeld,
    applyTaxEstimation,
  ]);

  const canCompute = computed.sell > 0 && computed.buy >= 0;

  return (
    <div className="page">
      <div className="hero">
        <h1>Calcul plus-value immobilière</h1>
        <p className="muted" style={{ margin: 0 }}>
          Estimation simplifiée selon durée de détention. Calcul 100% local.
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* Entrées */}
        <div className="card">
          <h2 className="cardTitle">Entrées</h2>

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Achat
          </h3>
          <label className="label">
            Prix d’achat (€)
            <input
              className="input"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Frais d’acquisition (€) (notaire…)
            <input
              className="input"
              value={purchaseFees}
              onChange={(e) => setPurchaseFees(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Travaux (€) (option)
            <input
              className="input"
              value={works}
              onChange={(e) => setWorks(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Autres frais achat (€) (option)
            <input
              className="input"
              value={otherCosts}
              onChange={(e) => setOtherCosts(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div style={{ height: 10 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Vente
          </h3>
          <label className="label">
            Prix de vente (€)
            <input
              className="input"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Frais d’agence (€) (option)
            <input
              className="input"
              value={agencyFees}
              onChange={(e) => setAgencyFees(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Autres frais vente (€) (option)
            <input
              className="input"
              value={otherSaleCosts}
              onChange={(e) => setOtherSaleCosts(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div style={{ height: 10 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Détention
          </h3>
          <label className="label">
            Durée de détention (années)
            <input
              className="input"
              value={yearsHeld}
              onChange={(e) => setYearsHeld(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginTop: 6,
            }}
          >
            <input
              type="checkbox"
              checked={applyTaxEstimation}
              onChange={(e) => setApplyTaxEstimation(e.target.checked)}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              Estimer impôt + prélèvements sociaux (mode simple)
            </span>
          </label>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn btnSecondary"
              onClick={() => {
                setPurchasePrice("180000");
                setPurchaseFees("13500");
                setWorks("0");
                setOtherCosts("0");
                setSalePrice("220000");
                setAgencyFees("0");
                setOtherSaleCosts("0");
                setYearsHeld("8");
                setApplyTaxEstimation(true);
              }}
            >
              Réinitialiser
            </button>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Estimation indicative : exonérations (résidence principale, prix
            faible, etc.) non gérées ici.
          </div>
        </div>

        {/* Résultats */}
        <div className="card">
          <h2 className="cardTitle">Résultats</h2>

          {!canCompute ? (
            <p className="muted">Renseigne au minimum un prix de vente.</p>
          ) : (
            <>
              <ResultRow
                label="Coût d’acquisition (total)"
                value={formatEUR(computed.acquisitionCost)}
              />
              <ResultRow
                label="Produit net vendeur (approx.)"
                value={formatEUR(computed.netSale)}
              />
              <ResultRow
                label="Plus-value brute"
                value={formatEUR(computed.grossGain)}
              />

              {computed.grossGain > 0 && applyTaxEstimation && (
                <>
                  <div style={{ height: 10 }} />
                  <ResultRow
                    label="Base taxable IR restante (approx.)"
                    value={`${computed.irBasePct.toFixed(0)} %`}
                  />
                  <ResultRow
                    label="Base taxable PS restante (approx.)"
                    value={`${computed.psBasePct.toFixed(0)} %`}
                  />
                  <ResultRow
                    label="Impôt estimé (IR ~ 19%)"
                    value={formatEUR(computed.estIR)}
                  />
                  <ResultRow
                    label="Prélèvements sociaux estimés (17,2%)"
                    value={formatEUR(computed.estPS)}
                  />
                  <ResultRow
                    label="Total fiscalité estimée"
                    value={formatEUR(computed.estTotalTax)}
                  />
                  <ResultRow
                    label="Plus-value nette estimée"
                    value={formatEUR(computed.netAfterTax)}
                  />
                </>
              )}

              {computed.grossGain <= 0 && (
                <div className="small" style={{ marginTop: 10 }}>
                  Ici la plus-value est nulle ou négative (pas d’imposition sur
                  plus-value).
                </div>
              )}

              <div style={{ height: 14 }} />

              <h3
                style={{
                  margin: "0 0 10px 0",
                  fontSize: 16,
                  letterSpacing: ".2px",
                }}
              >
                Détails
              </h3>

              <div
                className="card"
                style={{ padding: 12, background: "rgba(255,255,255,.06)" }}
              >
                <SmallLine
                  label="Durée de détention"
                  value={`${computed.years} ans`}
                />
                <SmallLine label="Prix achat" value={formatEUR(computed.buy)} />
                <SmallLine
                  label="Frais achat"
                  value={formatEUR(computed.buyFees)}
                />
                <SmallLine label="Travaux" value={formatEUR(computed.w)} />
                <SmallLine
                  label="Autres frais achat"
                  value={formatEUR(computed.buyOther)}
                />
                <SmallLine
                  label="Prix vente"
                  value={formatEUR(computed.sell)}
                />
                <SmallLine
                  label="Frais agence"
                  value={formatEUR(computed.ag)}
                />
                <SmallLine
                  label="Autres frais vente"
                  value={formatEUR(computed.sellOther)}
                />
              </div>

              <div className="small" style={{ marginTop: 10 }}>
                Modèle “simple” : pour un calcul exact, il faut appliquer les
                abattements officiels par année (IR/PS) + vérifier exonérations.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value }) {
  return (
    <div
      className="resultRow"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SmallLine({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "6px 0",
      }}
    >
      <span className="muted" style={{ fontSize: 13 }}>
        {label}
      </span>
      <strong style={{ fontSize: 13 }}>{value}</strong>
    </div>
  );
}
